import { LIMITS } from '@sbg/shared/constants';
import { SCENE_IDS } from './scenes/types';
import type { SceneId } from './scenes/types';

/** Texture scale: 0.5× – 2×, in steps of 0.05. */
export const SCALE_MIN = 0.5;
export const SCALE_MAX = 2;
export const SCALE_STEP = 0.05;

export type Rotation = 0 | 90;

/** Everything the visualizer shows, and exactly what its URL carries. */
export interface VisualizerState {
  scene: SceneId;
  /** Slug of the chosen stone, or `null` for "the default stone". */
  product: string | null;
  scale: number;
  rotate: Rotation;
  /** 0 – 100: how much of the stage, from the left, shows the stone (100 = all of it). */
  compare: number;
  /** Tile seams on floors and walls. */
  seams: boolean;
}

export const DEFAULT_STATE: VisualizerState = {
  scene: 'kitchen',
  product: null,
  scale: 1,
  rotate: 0,
  compare: 100,
  seams: false,
};

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const NUMBER = /^-?\d{1,6}(?:\.\d{1,6})?$/;

/** A slug the API could know: lower-case words joined by dashes, within the shared length limit. */
export function isSlug(value: string): boolean {
  return value.length > 0 && value.length <= LIMITS.slug.max && SLUG.test(value);
}

export function isSceneId(value: unknown): value is SceneId {
  return typeof value === 'string' && (SCENE_IDS as readonly string[]).includes(value);
}

/** A plain decimal number or `null` — never `NaN`, `Infinity`, hex, exponents or padded text. */
function plainNumber(raw: string | null): number | null {
  if (raw === null || !NUMBER.test(raw)) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

/** Round to the slider's step so a URL edited by hand lands on a value the slider can show. */
export const snapScale = (value: number): number =>
  Math.round(Math.min(SCALE_MAX, Math.max(SCALE_MIN, value)) / SCALE_STEP) * SCALE_STEP;

const cleanScale = (value: number): number => Number(snapScale(value).toFixed(2));

/**
 * The view described by a query string. The URL is user-editable, so nothing here can throw and
 * nothing is trusted: an unknown scene, a slug that is not a slug, a scale outside 0.5 – 2, a rotation
 * other than 0 or 90, a compare position outside 0 – 100 or anything that is not a plain number is
 * simply ignored (that field keeps its default) and the rest of the view is kept. When a parameter
 * repeats, the first value wins.
 */
export function parseVisualizerSearch(params: URLSearchParams): VisualizerState {
  const state: VisualizerState = { ...DEFAULT_STATE };

  const scene = params.get('scene');
  if (isSceneId(scene)) state.scene = scene;

  const product = params.get('product');
  if (product !== null && isSlug(product)) state.product = product;

  const scale = plainNumber(params.get('scale'));
  if (scale !== null && scale >= SCALE_MIN && scale <= SCALE_MAX) state.scale = cleanScale(scale);

  const rotate = params.get('rotate');
  if (rotate === '90') state.rotate = 90;

  const compare = plainNumber(params.get('compare'));
  if (compare !== null && compare >= 0 && compare <= 100) state.compare = Math.round(compare);

  const seams = params.get('seams');
  if (seams === '1' || seams === 'true') state.seams = true;

  return state;
}

export interface SerializeOptions {
  /**
   * Write `scene` and `product` even when they are the defaults. Used for links people share, which must
   * keep showing the same view whatever the default stone or scene becomes later.
   */
  explicit?: boolean;
  /** The stone to write when the state has none (the default stone that is on screen). */
  product?: string | null;
}

/**
 * The query string (without `?`) for a view. Defaults are left out, so the address bar stays clean
 * until the visitor changes something; parameters appear in a fixed order.
 */
export function serializeVisualizerState(
  state: VisualizerState,
  options: SerializeOptions = {},
): string {
  const params = new URLSearchParams();
  const product = state.product ?? (options.explicit ? (options.product ?? null) : null);
  if (options.explicit || state.scene !== DEFAULT_STATE.scene) params.set('scene', state.scene);
  if (product && isSlug(product)) params.set('product', product);
  if (state.scale !== DEFAULT_STATE.scale) params.set('scale', String(cleanScale(state.scale)));
  if (state.rotate !== DEFAULT_STATE.rotate) params.set('rotate', String(state.rotate));
  if (Math.round(state.compare) !== DEFAULT_STATE.compare) {
    params.set('compare', String(Math.round(Math.min(100, Math.max(0, state.compare)))));
  }
  if (state.seams) params.set('seams', '1');
  return params.toString();
}

/** Two views are the same view when they would write the same URL. */
export function sameView(a: VisualizerState, b: VisualizerState): boolean {
  return (
    serializeVisualizerState(a, { explicit: true }) ===
    serializeVisualizerState(b, { explicit: true })
  );
}
